import React, { useEffect, useRef, useState } from 'react'
import ReCAPTCHA from 'react-google-recaptcha'
import classNames from 'classnames'
import { captureException } from '@sentry/gatsby'
import { useFormik } from 'formik'
import { renderRichText } from 'gatsby-source-contentful/rich-text'
import {
  KeyValuePair,
  ContentfulFormTypes
} from '@toasttab/shared/src/interfaces/Interface.d'
import { getInitialFormValues } from '@toasttab/shared/src/hooks/useDataCapture'
import { extractFirstNameLastName } from '@toasttab/shared/src/utils/dataCaptureUtils'
import { useGeoLocation } from '@toasttab/shared/src/hooks/useGeoLocation'
import {
  getFormActionUrl,
  getUtmParams,
  postForm
} from '@toasttab/shared/src/utils/formUtils'
import _helpers from '@toasttab/shared/src/globals/_helpers'
import FormStep, {
  Field,
  Statement
} from '@toasttab/shared/src/components/molecules/Contentful/FormStep/FormStep'
import {
  FormDisclaimerOptions,
  FormDisclaimerOptionsInverse,
  FormDisclaimerOptionsEmailSignupInverse,
  FormDisclaimerEmailSignupOptions
} from '@toasttab/shared/src/utils/Contentful/richText'
import ThankYou from '@toasttab/shared/src/components/molecules/Contentful/ThankYou/ThankYou'
import { FormikContext } from '@toasttab/shared/src/hooks/useFormikContext'
import { recaptchaSiteKey } from '@toasttab/shared/utils/constants'
import { usePageContext } from '@toasttab/shared/src/hooks/usePageContext'
import { useNinetailed } from '@ninetailed/experience.js-gatsby'

export interface MainFormContentfulProps {
  ajaxTyCtas?: any
  ajaxTyDescription?: any
  ajaxTyHeading?: string
  ajaxTyIcon?: any
  ajaxTyShareIcons?: any
  ajaxTyShareIconsLabel?: string
  chiliPiperRouter?: string
  color?: 'default' | 'inverse'
  componentContext?: ContentfulFormTypes
  disclaimer?: any
  entryId?: string
  fieldIdPrefix?: string
  formCookie?: string
  formSteps: any
  formActionURL?:
    | 'marketo-integration/default/referral'
    | 'marketo-integration/default/post'
  heading?: string
  headingList?: any
  heapInteractionSubmit?: string
  isProgressiveEligibleForm: boolean
  layout?: 'default' | 'inline' | 'twoColumn' | 'fullWidth'
  marketoId: string
  marketoProgramName: string
  removePostalCode?: boolean
  thankYouPageURL?: string
  useRecaptcha?: boolean
}

const MainFormContentful = ({
  ajaxTyCtas,
  ajaxTyDescription,
  ajaxTyHeading,
  ajaxTyIcon,
  ajaxTyShareIcons,
  ajaxTyShareIconsLabel,
  chiliPiperRouter = '',
  color = 'default',
  componentContext = 'ContentfulMdsHero5050',
  disclaimer,
  entryId = '',
  fieldIdPrefix,
  formCookie,
  formSteps,
  formActionURL = 'marketo-integration/default/post',
  heading,
  headingList,
  heapInteractionSubmit,
  isProgressiveEligibleForm,
  layout = 'default',
  marketoId,
  marketoProgramName,
  removePostalCode = false,
  thankYouPageURL,
  useRecaptcha
}: MainFormContentfulProps) => {
  const [countryCode, setCountryCode] = useState(null)
  const [formActionUrl, setFormActionUrl] = useState(
    process.env.GATSBY_FORM_ACTION_URL
  )
  const [isError, setIsError] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isHandleSubmit, setIsHandleSubmit] = useState(false)
  const [postalCode, setPostalCode] = useState(null)
  const [region, setRegion] = useState('us')
  const [showThankYouAjax, setShowThankYouAjax] = useState(false)
  const [utmParameters, setUtmParameters] = useState({})
  const [isProgressiveForm, setIsProgressiveForm] = useState(null)
  // Recaptcha v3
  const [recaptchaResponse, setRecaptchaResponse] = useState(null)
  const recaptchaRef = useRef(null)
  const { identify } = useNinetailed()

  const { geoData, loading } = useGeoLocation()
  const { styleContext } = usePageContext()

  let disclaimerOptions =
    color === 'inverse' ? FormDisclaimerOptionsInverse : FormDisclaimerOptions
  if (componentContext === 'ContentfulMdsEmailSignup') {
    disclaimerOptions =
      color === 'inverse'
        ? FormDisclaimerOptionsEmailSignupInverse
        : FormDisclaimerEmailSignupOptions
  }

  const isAjax = ajaxTyHeading && ajaxTyDescription

  useEffect(() => {
    if (geoData) {
      setPostalCode(geoData?.postal?.code)
      setCountryCode(geoData?.country?.iso_code)
    }
  }, [loading])

  useEffect(() => {
    if (window?.location) {
      const currentRegion = window.location.pathname.split('/')[1]
      if (['ca', 'ie', 'uk'].includes(currentRegion)) {
        setRegion(currentRegion)
      }

      const formAction = getFormActionUrl(formActionURL)
      if (formAction !== formActionUrl) {
        setFormActionUrl(formAction)
      }
    }
  }, [])

  useEffect(() => {
    const utmParams = getUtmParams()
    if (Object.keys(utmParams).length > 0) {
      setUtmParameters(utmParams)
    }
  }, [])

  useEffect(() => {
    const progressiveCookie = _helpers.getCookie('progressiveEnabled')
    setIsProgressiveForm(progressiveCookie)
  }, [])

  // dynamically create initialValues for Formik
  const initValues: KeyValuePair = {
    _mkt_trk: `${_helpers.getCookie('_mkto_trk')}`,
    _mktoReferrer: `${typeof window !== 'undefined' && window.location.href}`,
    appendVariantTestText: '',
    browserLanguage: 'en',
    company: _helpers.getCookie('company'),
    cR: chiliPiperRouter, // Not currently in use anywhere, but needed in data
    // craftFieldsetSlug: formCategory.slug, // what do we do here? we don't have the same slug fields
    craftFormLocation: 'NOT SET',
    craftJavascriptStatus: 'Loaded',
    craftRestaurantToast: '', // honeypot, purposefully empty, if this is filled then assuming it's bot
    eI: entryId,
    email: _helpers.getCookie('email'),
    fC: formCookie,
    formid: marketoId,
    gae: '',
    isAjax: isAjax,
    mkoFullName: _helpers.getCookie('fullName'),
    mkoProgramName: marketoProgramName,
    munchkinId: `${process.env.GATSBY_FORM_MUNCHKINID ?? '713-DII-842'}`,
    phone: _helpers.getCookie('phonenumber'),
    postalCode,
    qQVersion: 'short-demo-request', // NEED TO REMOVE FOR REFERRAL FORM
    recaptchaValid: 'no',
    referrerURL: `${process.env.GATSBY_CMS_URL}${
      typeof window !== 'undefined' && window.location.pathname
    }`,
    referringPageURLText: `${
      typeof document !== 'undefined' && document.referrer
    }`,
    returnURL: thankYouPageURL ?? '',
    shownCalendly: '',
    step: 0,
    toastType: '' // NEED TO REMOVE FOR REFERRAL FORM
  }
  // set up additional standard initial marketo form values
  const { initialValues } = getInitialFormValues(initValues)

  const validateFormSubmission = (values: KeyValuePair) => {
    const errors: KeyValuePair = {}

    const validateFormInput = (field: Field | Statement) => {
      if (field.__typename === 'ContentfulMdsFormField') {
        const f = field as Field
        const fieldName = f.fieldInputNameMarketoRestApi
        if (f.isRequired && !values[fieldName]) {
          errors[fieldName] = `${f.fieldLabel} is required`
        } else if (f.fieldType === 'Email') {
          if (_helpers.validateEmail(values[fieldName])) {
            errors.email = 'Invalid email address'
          } else {
            // @ts-ignore - Typescript doesn't like setting new properties on window
            let heapHandler = window.heap
            if (
              typeof window !== 'undefined' &&
              typeof heapHandler !== 'undefined'
            ) {
              heapHandler.addUserProperties({
                email: values[fieldName].toLowerCase()
              })
              heapHandler.identify(values[fieldName].toLowerCase())
            }
          }
        } else if (f.fieldType === 'Phone') {
          // currently otl doesnt validate phone
          const phoneInput = fieldName
          if (_helpers.validatePhoneInput(values[phoneInput], region)) {
            errors[fieldName] = 'Invalid phone number'
          } else if (['ca', 'us'].includes(region)) {
            const formattedPhone = _helpers.formatPhoneNumber(
              values[phoneInput]
            )
            if (formattedPhone !== values[phoneInput]) {
              formik.setFieldValue(phoneInput, formattedPhone)
            }
          }
        } else if (f.fieldType === 'Checkbox (Single/Multi Select)') {
          const checkboxInput = fieldName
          const checkboxValues = values[checkboxInput]
          if (f.isRequired && !checkboxValues) {
            errors[fieldName] = `${f.fieldLabel} is required`
          } else {
            if (!checkboxValues) {
              if (f.fieldOptions.length === 1) {
                formik.setFieldValue(checkboxInput, false, false)
              } else {
                formik.setFieldValue(checkboxInput, [], false)
              }
            }
          }
        } else if (fieldName === 'mkoFullName') {
          const fieldValue = values[fieldName]
          if (
            fieldValue.indexOf(' ') === -1 ||
            [' ', '-'].includes(fieldValue.charAt(fieldValue.length - 1)) ||
            /[^a-zA-Z\u00c0-\u024f\u1e00-\u1eff\- ]/g.test(fieldValue)
          ) {
            errors[
              f.fieldInputNameMarketoRestApi
            ] = `${f.fieldLabel} is required`
          }
        }
      }
    }

    if (!isProgressiveForm || formSteps.length > 1) {
      const coreFields = formSteps[formik.values.step].coreFields
      coreFields &&
        coreFields.map((field: Field | Statement) => {
          validateFormInput(field)
        })
    } else if (isProgressiveForm && isProgressiveEligibleForm) {
      const progressiveFields = formSteps[formik.values.step].progressiveFields

      progressiveFields &&
        progressiveFields.map((field: Field | Statement) => {
          validateFormInput(field)
        })
    } else {
      const coreFields = formSteps[formik.values.step].coreFields
      coreFields &&
        coreFields.map((field: Field | Statement) => {
          validateFormInput(field)
        })
    }
    return errors
  }

  const renderFormSteps = (formSteps: any, handleSubmit: any) => {
    const numSteps = formSteps.length
    return formSteps.map((step: any, index: number) => (
      <FormStep
        {...step}
        color={color}
        componentContext={componentContext}
        fieldIdPrefix={fieldIdPrefix}
        formik={formik}
        handleSubmit={handleSubmit}
        heapInteractionSubmit={heapInteractionSubmit}
        isActive={
          (!showThankYouAjax && index === formik.values.step) ||
          (showThankYouAjax && index + 1 === numSteps) ||
          (index === 0 && numSteps === 1)
        }
        isLastStep={index + 1 === formSteps.length}
        isProgressiveEligibleForm={isProgressiveEligibleForm}
        isProgressiveForm={isProgressiveForm}
        isSubmitting={isSubmitting}
        key={index}
        layout={layout}
        numSteps={numSteps}
      />
    ))
  }

  // Pass the useFormik() hook initial form values and a submit function that will
  // be called when the form is submitted
  // const formikContext = useFormikContext()
  const formik = useFormik({
    initialValues: initialValues,
    enableReinitialize: false,
    validate: validateFormSubmission,
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit: (values) => {
      if (isSubmitting) return
      const mkoFullName = values['mkoFullName']
      const extractedName = mkoFullName && extractFirstNameLastName(mkoFullName)

      if (formActionURL === 'marketo-integration/default/referral') {
        // Needed to set up data correctly for referrals
        delete values['firstName']
        delete values['lastName']
      } else {
        values['firstName'] = extractedName?.FirstName
        values['lastName'] = extractedName?.LastName
      }

      let formData = new FormData()
      formData.append('craftInferredCountry', _helpers.getCountry(countryCode))
      if (useRecaptcha) {
        formData.append('g-recaptcha-response', recaptchaResponse)
      }

      for (let value in values) {
        if (value === 'mkoFullName') {
          if (extractedName?.FirstName) {
            _helpers.createCookie('fN', extractedName.FirstName, 525600, '/')
          }
          if (extractedName?.LastName) {
            _helpers.createCookie('lN', extractedName.LastName, 525600, '/')
          }
          if (extractedName?.FullName) {
            _helpers.createCookie(
              'fullName',
              extractedName.FullName,
              525600,
              '/'
            )
          }
        } else if (value === 'email') {
          _helpers.createCookie('email', values[value], 525600, '/')
        } else if (value === 'company') {
          _helpers.createCookie('company', values[value], 525600, '/')
        } else if (value === 'phone') {
          _helpers.createCookie('phonenumber', values[value], 525600, '/')
        } else if (value === 'postalCode' && values[value]) {
          _helpers.createCookie('_userUpdatedLoc', values[value], 525600, '/')
        } else if (value === 'mkoProgramName') {
          if (
            values[value].toLowerCase() === 'Website-Demo-Request'.toLowerCase()
          ) {
            _helpers.createCookie('toastDemoForm', '1', 525600, '/')
          }
        } else {
          _helpers.createCookie('toastResourceForm', '1', 900, '/')
          _helpers.createCookie('progressiveEnabled', '1', 43200, '/')
        }
        // Only when Toast Type exists
        if (values['toastType']) {
          let regionString = ''
          if (region !== 'us') {
            regionString = `/${region}`
          }
          if (value === 'returnURL') {
            let returnUrl = `${regionString}/request-demo-confirmed`
            if (values['toastType'] === "I'm already a Toast customer") {
              returnUrl = `${regionString}/thank-you/toast-confirmed?customer=true`
            } else if (
              values['toastType'] === "I'm a restaurant guest" ||
              values['toastType'] === "I'm a retail guest"
            ) {
              returnUrl = `${regionString}/thank-you/toast-confirmed?customer=false`
            }
            formData.append(value, returnUrl)
          } else {
            const formValue = [null, undefined].includes(values[value])
              ? ''
              : values[value]
            formData.append(value, formValue)
          }
        } else {
          const formValue = [null, undefined].includes(values[value])
            ? ''
            : values[value]
          formData.append(value, formValue)
        }
      }

      if (!isAjax) {
        // We need to delete this value entirely for the POST request
        // If it's set at all, even to null or false, it triggers some unwanted PHP and fails the form submission
        formData.delete('isAjax')
      }

      setIsSubmitting(true)

      if (isAjax) {
        formData.append('responseTemplate', '') // This is a hack to get the form to submit without a response template to cause no errors in the console.
        setShowThankYouAjax(true)
      }
      postForm(formData, formActionUrl)
        .then((response) => {
          // use ninetailed to identify the form submission
          identify('', { mqled: 'formSubmitted' })
          setIsSubmitting(false)
          if (response.redirected) {
            setIsSubmitted(true)
            // navigate(response.url.replace('https://phrasingpro3.com', ''))
            // @ts-ignore
            // Typescript doesn't like setting a window.location as a string, despite it being common practice
            // https://github.com/microsoft/TypeScript/issues/48949
            window.location = response.url
          }
        })
        .catch(function (err) {
          console.log(err)
          captureException(`${err} - ${JSON.stringify(values, null, 2)}`)
          setIsSubmitting(false)
          setIsError(true)
        })
    }
  })

  useEffect(() => {
    const { initialValues } = getInitialFormValues(initValues, utmParameters)
    formik.setValues({ ...initialValues }, false)
  }, [utmParameters])

  const handleRecaptcha = (e: any) => {
    setRecaptchaResponse(e)
    if (formik.isValid) {
      setTimeout(() => {
        formik.handleSubmit()
      }, 100)
    }
  }

  const handleSubmit = async (e: any) => {
    e?.preventDefault()
    if (isHandleSubmit) return

    setIsHandleSubmit(true)
    try {
      if (useRecaptcha) {
        await recaptchaRef.current.execute()
      } else {
        await formik.handleSubmit()
      }
    } catch {
      console.log('Error: Submission Failure')
    } finally {
      setIsHandleSubmit(false)
    }
  }

  const disclaimerClasses = classNames('mt-4 [&_p:not(:last-child)]:mb-4', {
    'text-center':
      ['fullWidth', 'twoColumn'].includes(layout) ||
      styleContext === 'request-demo'
  })

  if (formSteps.length > 0 && formSteps.length < 2) {
    formSteps[0].heading = heading
    formSteps[0].headingList = headingList
  }

  return (
    <>
      <div
        className={showThankYouAjax ? 'invisible' : ''}
        data-testid='MainFormContentful'
      >
        <FormikContext.Provider value={{ formik: formik }}>
          <form
            id={`mktForm_${marketoId}`}
            data-heap-mktform-id={marketoId}
            data-heap-
            aria-label='Main Form'
            data-testid={isSubmitted ? 'form-submitted' : 'form-not-submitted'}
            onSubmit={handleSubmit}
          >
            {useRecaptcha && (
              <ReCAPTCHA
                onChange={handleRecaptcha}
                ref={recaptchaRef}
                size='invisible'
                sitekey={recaptchaSiteKey}
              />
            )}
            {formSteps &&
              formSteps.length > 0 &&
              renderFormSteps(formSteps, handleSubmit)}

            <input
              type='hidden'
              name='GCLID__c'
              id='GCLID__c'
              value={formik.values.GCLID__c ?? ''}
            />
            <input
              type='hidden'
              name='Website_Source_Detail__c'
              id='Website_Source_Detail__c'
              value={formik.values.Website_Source_Detail__c ?? ''}
            />
            <input
              type='hidden'
              id='craftFieldsetSlug'
              name='craftFieldsetSlug'
              value={formik.values.craftFieldsetSlug ?? ''}
            />
            <input
              type='hidden'
              id='Last_Blog_Seen__c'
              name='Last_Blog_Seen__c'
              value={formik.values.Last_Blog_Seen__c ?? ''}
            />
            <input
              type='hidden'
              id='Web_initiative__c'
              name='Web_initiative__c'
              value={formik.values.Web_initiative__c ?? ''}
            />
            <input
              type='hidden'
              id='Web_origin__c'
              name='Web_origin__c'
              value={formik.values.Web_origin__c ?? ''}
            />
            <input
              type='hidden'
              id='Web_detail__c'
              name='Web_detail__c'
              value={formik.values.Web_detail__c ?? ''}
            />

            <input
              type='hidden'
              id='_mktoReferrer'
              name='_mktoReferrer'
              value={formik.values._mktoReferrer ?? ''}
            />
            <input
              type='hidden'
              id='pageFormSubmissionURL'
              name='pageFormSubmissionURL'
              value={formik.values.pageFormSubmissionURL ?? ''}
            />
            <input
              type='hidden'
              id='eI'
              name='eI'
              value={formik.values.eI ?? ''}
            />
            <input
              type='hidden'
              id='browserLanguage'
              name='browserLanguage'
              value={formik.values.browserLanguage ?? ''}
            />
            <input
              type='hidden'
              id='referrerURL'
              name='referrerURL'
              value={formik.values.referrerURL ?? ''}
            />
            <input
              type='hidden'
              id='returnURL'
              name='returnURL'
              value={formik.values.returnURL}
            />
            <input
              type='hidden'
              id='munchkinId'
              name='munchkinId'
              value={formik.values.munchkinId}
            />
            <input
              type='hidden'
              id='_mkt_trk'
              name='_mkt_trk'
              value={formik.values._mkt_trk}
            />
            <input
              type='hidden'
              id='mkoProgramName'
              name='mkoProgramName'
              value={formik.values.mkoProgramName}
            />
            <input name='formid' type='hidden' value={formik.values.formid} />
            <input
              type='hidden'
              id='craftInferredCountry'
              name='craftInferredCountry'
              value={_helpers.getCountry(countryCode) ?? ''}
            />
            {!removePostalCode && (
              <input
                type='hidden'
                id='postalCode'
                name='postalCode'
                value={postalCode ?? ''}
              />
            )}
            <input
              type='hidden'
              id='qQVersion'
              name='qQVersion'
              value={formik.values.qQVersion ?? ''}
            />
            <input
              type='hidden'
              id='fC'
              name='fC'
              value={formik.values.fC ?? ''}
            />
            <input
              type='hidden'
              id='gae'
              name='gae'
              value={formik.values.gae ?? ''}
            />
            <input
              type='hidden'
              id='reffering-page-url'
              name='referringPageURLText'
              value={formik.values.referringPageURLText}
            />

            <input
              type='hidden'
              id='craftJavascriptStatus'
              name='craftJavascriptStatus'
              value={formik.values.craftJavascriptStatus}
            />
            <input
              type='hidden'
              id='shownCalendly'
              name='shownCalendly'
              value={formik.values.shownCalendly}
            />
            <input
              type='hidden'
              id='recaptchaValid'
              name='recaptchaValid'
              value={formik.values.recaptchaValid}
            />
            <input
              type='hidden'
              name='progressiveEligible'
              id='progressiveEligible'
              value={formik.values.progressiveEligible}
            />
            <input
              type='hidden'
              id='appendVariantTestText'
              name='appendVariantTestText'
              value={formik.values.appendVariantTestText}
            />
            <input
              type='hidden'
              id='craftFormLocation'
              name='craftFormLocation'
              value={formik.values.craftFormLocation}
            />
            <input type='hidden' id='cR' name='cR' value={formik.values.cR} />
            <input
              type='hidden'
              name='craftRestaurantToast'
              id='craftRestaurantToast'
              value=''
            />
            <input
              type='hidden'
              id='action'
              name='action'
              value={formActionUrl}
            />
          </form>
        </FormikContext.Provider>
        {isError && (
          <div className='text-error text-xs relative text-left top-0'>
            There was an error trying to submit the form.
          </div>
        )}
        {disclaimer && (
          <div className={disclaimerClasses} data-testid='MainForm-disclaimer'>
            {renderRichText(disclaimer, disclaimerOptions)}
          </div>
        )}
      </div>
      {isAjax && (
        <ThankYou
          showThankyou={showThankYouAjax}
          ajaxTyIcon={ajaxTyIcon}
          ajaxTyHeading={ajaxTyHeading}
          ajaxTyDescription={ajaxTyDescription}
          ajaxTyCtas={ajaxTyCtas}
          ajaxTyShareIconsLabel={ajaxTyShareIconsLabel}
          ajaxTyShareIcons={ajaxTyShareIcons}
          color={color}
        />
      )}
    </>
  )
}

export default MainFormContentful
